import { useState } from 'react';
import { useIntl } from 'react-intl';
import { useLoaderData, useNavigate, useRevalidator } from 'react-router-dom';
import { Button, Label, Modal, Textarea } from '@immo/ui-kit';
import type { SystemSkillView } from '@immo/api-contract';
import { apiClient } from '@/lib/auth/api-client';
import { useTranslations } from '@/i18n/use-translations';
import { PageTopbar } from '@/components/layout/topbar-slots';
import { BreadcrumbLeading } from '@/components/layout/page-leading';
import { TagIcon } from '@/components/layout/icons';

export async function systemSkillEditorLoader(): Promise<SystemSkillView> {
  const result = await apiClient.adminSystemSkills.get();

  if (!result.ok) {
    throw new Response('Failed to load the fallback skill', { status: result.status });
  }

  return result.body;
}

function errorCodeOf(body: unknown): string | undefined {
  return (body as { error?: { code?: string } } | undefined)?.error?.code;
}

export function SystemSkillEditorPage() {
  const t = useTranslations('pages.admin.skills.document-analyses.fallback-editor');
  const tList = useTranslations('pages.admin.skills.document-analyses');
  const intl = useIntl();
  const view = useLoaderData() as SystemSkillView;
  const navigate = useNavigate();
  const revalidator = useRevalidator();

  const [prompt, setPrompt] = useState(view.prompt);
  const [inlineError, setInlineError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);
  const [busy, setBusy] = useState(false);
  const [staleOpen, setStaleOpen] = useState(false);
  const [syncedView, setSyncedView] = useState({ prompt: view.prompt, version: view.version });

  // Re-sync the field whenever the server view changes (after a save or a
  // stale-edit reload) so the editor reflects the persisted state. The list/
  // editor must not re-enter a loading state on revalidation (BL-011): the
  // loader data stays mounted and only the field value is swapped. Done during
  // render, not in an effect: a mount effect is a passive flush that can land
  // after the operator's first keystroke and silently restore the stored prompt.
  if (syncedView.prompt !== view.prompt || syncedView.version !== view.version) {
    setSyncedView({ prompt: view.prompt, version: view.version });
    setPrompt(view.prompt);
    setInlineError(null);
  }

  const goToList = () => navigate('/admin/skills/document-analyses');

  const handleResponse = (result: { ok: boolean; status: number; body: unknown }): boolean => {
    if (result.ok) {
      setSaved(true);
      revalidator.revalidate();

      return true;
    }

    if (result.status === 409) {
      setStaleOpen(true);

      return false;
    }

    const code = errorCodeOf(result.body);

    if (code === 'admin.skill.prompt-empty') setInlineError(t('errors.prompt-empty'));
    else if (code === 'admin.skill.prompt-too-long') setInlineError(t('errors.prompt-too-long'));
    else setInlineError(t('errors.generic'));

    return false;
  };

  const save = async () => {
    setInlineError(null);
    setSaved(false);

    if (prompt.trim().length === 0) {
      setInlineError(t('errors.prompt-empty'));

      return;
    }

    setBusy(true);

    try {
      const result = await apiClient.adminSystemSkills.save({
        body: { prompt, expectedVersion: view.version },
      });
      handleResponse(result);
    } finally {
      setBusy(false);
    }
  };

  const reloadAfterStale = () => {
    setStaleOpen(false);
    revalidator.revalidate();
  };

  const attribution =
    view.lastEditedByName !== null
      ? t('last-edited-by', {
          name: view.lastEditedByName,
          date: intl.formatDate(view.lastEditedAt, { dateStyle: 'short' }),
        })
      : t('system-attribution');

  return (
    <>
      <PageTopbar
        leading={
          <BreadcrumbLeading
            icon={TagIcon}
            crumbs={[{ label: tList('title'), to: '/admin/skills/document-analyses' }, { label: t('title') }]}
          />
        }
      />
      <div className="mx-auto max-w-3xl space-y-4 p-6">
        <div className="flex items-center gap-3">
          <h1 className="text-lg font-semibold text-text-primary">{t('title')}</h1>
          <StatusBadge label={t('status')} />
        </div>
        <p className="text-sm text-text-tertiary">{attribution}</p>

        <div className="space-y-1">
          <Label htmlFor="system-skill-prompt">{t('prompt-label')}</Label>
          <Textarea
            id="system-skill-prompt"
            value={prompt}
            onChange={(event) => {
              setPrompt(event.target.value);
              setInlineError(null);
              setSaved(false);
            }}
            disabled={busy}
            rows={16}
            // A prompt is code-like text: mono and denser than a form field.
            className="bg-canvas font-mono text-sm"
            invalid={inlineError !== null}
            aria-describedby={inlineError !== null ? 'system-skill-prompt-error' : undefined}
          />
          {inlineError !== null && (
            <p id="system-skill-prompt-error" className="text-sm text-danger-600">
              {inlineError}
            </p>
          )}
          {saved && <p className="text-sm text-success-700">{t('saved')}</p>}
        </div>

        {/* No deactivate control — the fallback is the analysis floor. */}
        <div className="flex items-center gap-2">
          <Button type="button" onClick={save} disabled={busy}>
            {t('save')}
          </Button>
          <Button type="button" variant="ghost" onClick={goToList} disabled={busy}>
            {t('cancel')}
          </Button>
        </div>
      </div>

      <Modal
        open={staleOpen}
        onClose={() => setStaleOpen(false)}
        size="sm"
        title={t('stale.title')}
        footer={
          <Button type="button" onClick={reloadAfterStale}>
            {t('stale.reload')}
          </Button>
        }
      >
        <p className="text-sm text-text-secondary">{t('stale.body')}</p>
      </Modal>
    </>
  );
}

function StatusBadge({ label }: { readonly label: string }) {
  return (
    <span className="inline-flex items-center rounded-full bg-success-50 px-2 py-0.5 text-xs font-medium text-success-700">
      {label}
    </span>
  );
}
